import { inject, injectable } from 'inversify';
import React from 'react';

import { PatchUserPayload } from '@project44/iam-team-ui-utils/src/domains/User/domain/UserServiceClientAdapter';
import { User } from '@project44/ui-utils';
import {
  DtoBase,
  TenantModels,
  UserModels,
} from '@project44-models/iam-gateway';

import { ReactHookServicePort } from '../../../../shared/core/port/React.hook.service.port';
import { UserUIService } from '../../application/User.ui.service';
import { TUseUserCreator } from '../../domain/interfaces';
import { UserDetailsRoute } from '../../domain/routes/UserDetails.route';
import { UserServiceController } from '../controllers/User.service.controller';

@injectable()
export class UserUIServicePort {
  constructor(
    @inject(UserUIService)
    private readonly userUIService: UserUIService,

    @inject(UserServiceController)
    private readonly userServiceController: UserServiceController,

    @inject(ReactHookServicePort)
    private readonly reactHookServicePort: ReactHookServicePort,
  ) {}

  public readonly composeUserUnifiedRolesTenantAgnosticAggregate = (payload: {
    userPrincipal: User;
    user: DtoBase<UserModels.UserViewModel>;
    tenant: DtoBase<TenantModels.TenantViewModel>;
  }) => {
    return this.userUIService.composeUserUnifiedRolesTenantSpecificAggregate(
      payload,
    );
  };

  public readonly composeUserUnifiedRolesTenantSpecificAggregate = (payload: {
    userPrincipal: User;
    user: DtoBase<UserModels.UserViewModel>;
    tenant: DtoBase<TenantModels.TenantViewModel>;
  }) => {
    return this.userUIService.composeUserUnifiedRolesTenantSpecificAggregate(
      payload,
    );
  };

  public readonly composeUserCreationForm: React.FC<{
    userPrincipal: User;
    useUserCreatorConfig: ReturnType<TUseUserCreator>;
    afterUserCreationActionHandler: (
      user: DtoBase<UserModels.UserViewModel>,
    ) => void;
  }> = ({
    userPrincipal,
    afterUserCreationActionHandler,
    useUserCreatorConfig,
  }) => {
    return this.userUIService.composeUserCreationForm({
      userPrincipal,
      useUserCreatorConfig,
      afterUserCreationActionHandler,
    });
  };

  public readonly composeUserDetailsRoute = (payload: {
    userPrincipal: User;
    user: DtoBase<UserModels.UserViewModel>;
    tenant: DtoBase<TenantModels.TenantViewModel>;
    updateUserDetailsHandler: (userDetails: PatchUserPayload) => Promise<void>;
  }): UserDetailsRoute => {
    const { updateUserDetailsHandler, userPrincipal, user, tenant } = payload;

    return this.userUIService.composeUserDetailsRoute({
      userPrincipal,
      user,
      tenant,
      getUserDetails: async (payload) => {
        return this.userServiceController.getUserById(userPrincipal, payload);
      },
      updateUserDetails: async (payload) => {
        return updateUserDetailsHandler(payload);
      },
      composeUseApiRequestWrapper:
        this.reactHookServicePort.composeApiRequestHook,
    });
  };
}
